// ============================================================
// useDiagUpload - a seat carries its last lines (v8.362.0).
//
// The 2026-09-16 session: players lost characters and had to rejoin, and
// the only log anybody could copy was the GM's, which was clean. Every
// client already keeps a ring buffer (diagnostics/log); this puts the
// newest lines in the client's OWN `seatDiag` document (2026-10-31: off
// the seat, where every heartbeat delivered them to every screen), which
// the GM's bug report reads when it is copied - so one report holds what
// every browser at the table saw.
//
// WHEN: 5 s after joining (the seat announces itself), 5 s after any
// WARN or ERROR (coalesced - a burst is one upload), and otherwise once
// a minute when there is something new. Quiet costs nothing.
//
// Under the account's uid, which the rules bind the document to: a
// member writes their own, and only the GM reads anyone else's.
// ============================================================
import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { diag as appDiag, errorSummary, type Diagnostics, type DiagEntry } from '../diagnostics/log';
import { formatSeatDiag } from '../diagnostics/report';

export const DIAG_UPLOAD_SOON_MS = 5000;
export const DIAG_UPLOAD_PERIOD_MS = 60000;

interface Params {
  sessionId: string | null;
  /** The account's uid - the document's id and owner. Null: nothing is uploaded (the GM's lines are its own). */
  uid: string | null;
  setSeatDiag: (sessionId: string, uid: string, doc: { diag: string; diagAt: number }) => Promise<unknown>;
  /** Injected in tests; the app's buffer otherwise. */
  diag?: Diagnostics;
  now?: () => number;
}

interface Uploads {
  sessionId: string;
  uid: string;
  setSeatDiag: Params['setSeatDiag'];
  diag: Diagnostics;
  now: () => number;
  /** Something new since the last upload. */
  dirty: MutableRefObject<boolean>;
}

/** One upload at a time, none after `stop()`. A failure marks the lines dirty again. */
function makeUploader({ sessionId, uid, setSeatDiag, diag, now, dirty }: Uploads) {
  let stopped = false;
  let uploading = false;
  const upload = () => {
    if (stopped || uploading) return;
    uploading = true;
    dirty.current = false;
    const text = formatSeatDiag(diag.entries());
    setSeatDiag(sessionId, uid, { diag: text, diagAt: now() })
      .catch((e: unknown) => {
        // Not through diag.warn: an upload that fails because the
        // connection is down would then schedule another upload of the
        // failure, for ever. The console is enough - the next line the
        // app records will queue a fresh attempt.
        console.warn('[diag] seat upload failed', errorSummary(e));
        dirty.current = true;
      })
      .finally(() => {
        uploading = false;
      });
  };
  const stop = () => {
    stopped = true;
  };
  return { upload, stop };
}

/** `fn` once, DIAG_UPLOAD_SOON_MS from the first request - a burst of requests is one call. */
function makeSoon(fn: () => void) {
  let soon: ReturnType<typeof setTimeout> | null = null;
  const schedule = () => {
    if (soon) return;
    soon = setTimeout(() => {
      soon = null;
      fn();
    }, DIAG_UPLOAD_SOON_MS);
  };
  const cancel = () => {
    if (soon) clearTimeout(soon);
  };
  return { schedule, cancel };
}

/** Start the uploads for one seat; the cleanup stops them. */
function startUploads(ctx: Uploads): () => void {
  const uploader = makeUploader(ctx);
  const soon = makeSoon(uploader.upload);
  const unsubscribe = ctx.diag.subscribe((entry: DiagEntry) => {
    ctx.dirty.current = true;
    if (entry.level !== 'info') soon.schedule();
  });
  soon.schedule(); // the seat announces itself
  const tick = setInterval(() => {
    if (ctx.dirty.current) uploader.upload();
  }, DIAG_UPLOAD_PERIOD_MS);
  return () => {
    uploader.stop();
    unsubscribe();
    soon.cancel();
    clearInterval(tick);
  };
}

export default function useDiagUpload({ sessionId, uid, setSeatDiag, diag = appDiag, now = Date.now }: Params): void {
  const dirty = useRef(false);
  useEffect(() => {
    if (!sessionId || !uid) return;
    return startUploads({ sessionId, uid, setSeatDiag, diag, now, dirty });
  }, [sessionId, uid, setSeatDiag, diag, now]);
}
